/**
 * Figures de CONTEXTE des automatismes 4e Pythagore : 1088 « L'échelle »
 * (une échelle contre un mur), 1089 « Droit ou pas ? » (une étagère sur un
 * mur, un mur sur le sol). Modèles : les exemples de réussite Eduscol de fin
 * de 4e (l. 420-431 : l'échelle de 13 m, l'étagère d'Alan) et la fiche 8 du
 * professeur (le mur des maçons).
 *
 * Dessins EXACTS (décision prof 2026-09-24) : les longueurs dessinées sont
 * proportionnelles aux longueurs annoncées, à une échelle libre (la règle ne
 * donne jamais la réponse) ; un angle qui n'est pas droit est dessiné tel
 * quel, à quelques degrés près il ne se voit pas : c'est le calcul qui
 * tranche. Seule l'échelle code son angle droit (mur vertical, sol
 * horizontal : c'est une donnée) ; l'étagère et le mur ne codent rien
 * (l'angle droit est la question).
 *
 * Chaque figure porte data-mesures (« nom:écriture|… ») et data-figure
 * (points en coordonnées abstraites) pour les solveurs et les tests.
 */

const W = 320; const H = 240;
const f1 = (v) => v.toFixed(1);
const ENCRE = '#334155';
const GRIS = '#64748B';
const COULEUR_COTE = '#0369A1';
const COULEUR_CHERCHE = '#EA580C';
const MUR = '#E2E8F0';
const JOINT = '#94A3B8';
const BOIS = '#B45309';
const HAUTEUR_TEXTE = 13;

const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const norme = (u) => Math.hypot(u[0], u[1]);
const unitaire = (u) => { const L = norme(u) || 1; return [u[0] / L, u[1] / L]; };
const perp = (u) => [-u[1], u[0]];
const add = (P, u, k = 1) => [P[0] + u[0] * k, P[1] + u[1] * k];
/** Largeur approchée d'une écriture en Arial 13 px (même table que figTriangle.js). */
const largeurTexte = (t) => [...String(t)].reduce((s, c) => s + (c === ' ' || c === ',' || c === '.' ? 3.6 : c === 'm' ? 10.8 : c === '°' ? 5.2 : 7.3), 0);

const ecriture = (C, t, cherche, nom) => `<text x="${f1(C[0])}" y="${f1(C[1] + 4.5)}" font-size="13" font-weight="600" fill="${cherche ? COULEUR_CHERCHE : COULEUR_COTE}" text-anchor="middle" data-mesure="${nom}">${t}</text>`;
const trait = (P, Q, couleur, largeur, extra = '') => `<line x1="${f1(P[0])}" y1="${f1(P[1])}" x2="${f1(Q[0])}" y2="${f1(Q[1])}" stroke="${couleur}" stroke-width="${largeur}"${extra}/>`;

/** Une écriture posée à côté du point M, du côté de la normale n, sans toucher le trait. */
const aCote = (M, n, t, ecart = 6) => {
    const w = largeurTexte(t);
    const d = ecart + Math.abs(n[0]) * (w / 2) + Math.abs(n[1]) * (HAUTEUR_TEXTE / 2);
    return add(M, n, d);
};

/** Trait de cote de P à Q (pixels) avec taquets, et l'écriture au milieu, du côté n. */
const cote = (P, Q, n, t, cherche, nom) => {
    const u = unitaire(vec(P, Q)); const q = perp(u);
    return trait(P, Q, GRIS, 1)
        + trait(add(P, q, -4), add(P, q, 4), GRIS, 1)
        + trait(add(Q, q, -4), add(Q, q, 4), GRIS, 1)
        + ecriture(aCote([(P[0] + Q[0]) / 2, (P[1] + Q[1]) / 2], n, t), t, cherche, nom);
};

/** Sol horizontal de x1 à x2 (pixels) à la hauteur y, hachuré dessous. */
const sol = (x1, x2, y) => {
    let s = trait([x1, y], [x2, y], ENCRE, 2);
    for (let x = x1 + 6; x <= x2; x += 9) s += trait([x, y], [x - 6, y + 7], JOINT, 1);
    return s;
};

/** À gauche d'une direction u à l'écran (y vers le bas) : u tourné d'un quart de tour. */
const gauche = (u) => [u[1], -u[0]];

/** Mur : bande d'épaisseur e à gauche du segment P → Q (P en bas), avec des joints. */
const mur = (P, Q, e = 16) => {
    const u = unitaire(vec(P, Q)); const n = gauche(u);
    const P2 = add(P, n, e); const Q2 = add(Q, n, e);
    let s = `<polygon points="${[P, Q, Q2, P2].map((p) => `${f1(p[0])},${f1(p[1])}`).join(' ')}" fill="${MUR}" stroke="${GRIS}" stroke-width="1"/>`;
    const L = norme(vec(P, Q));
    for (let k = 14; k < L; k += 14) s += trait(add(P, u, k), add(P2, u, k), JOINT, 0.8);
    return s;
};

const point = (P, nom, dir) => {
    const d = unitaire(dir);
    return `<circle cx="${f1(P[0])}" cy="${f1(P[1])}" r="2.6" fill="#1e293b"/>`
        + `<text x="${f1(P[0] + d[0] * 14)}" y="${f1(P[1] + d[1] * 14 + 5)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${nom}</text>`;
};

const dataMesures = (mesures) => Object.entries(mesures).map(([k, v]) => `${k}:${v}`).join('|');

/**
 * L'échelle contre un mur vertical, sur un sol horizontal. d = distance du pied
 * de l'échelle au pied du mur, h = hauteur atteinte (m), mesures = écritures
 * { d, h, L } (« ? » pour la cherchée ; h à null : pas de cote de hauteur),
 * cherche = 'd' | 'h' | 'L' | 'angle' ; angle = écriture de l'angle au pied
 * de l'échelle, entre le sol et l'échelle (1090 : l'exemple Eduscol l. 420-427).
 */
export const svgEchelle = ({ d, h, mesures, cherche, angle = null }) => {
    const estCherche = (k) => [].concat(cherche).includes(k);
    const padL = 86; const padR = 46; const padT = 26; const padB = 52;
    const s = Math.min((W - padL - padR) / d, (H - padT - padB) / h);
    const x0 = padL + ((W - padL - padR) - d * s) / 2;
    const X = (x) => x0 + x * s; const Y = (y) => H - padB - y * s;
    const pied = [X(d), Y(0)]; const haut = [X(0), Y(h)]; const coin = [X(0), Y(0)];
    let out = mur(coin, [X(0), Y(h) - 18]);
    out += sol(X(0) - 16, X(d) + 28, Y(0));
    out += `<polyline points="${f1(coin[0] + 10)},${f1(coin[1])} ${f1(coin[0] + 10)},${f1(coin[1] - 10)} ${f1(coin[0])},${f1(coin[1] - 10)}" fill="none" stroke="#111827" stroke-width="1.6" data-code="carre"/>`;
    // l'échelle : deux montants et des barreaux
    const u = unitaire(vec(pied, haut)); const n = perp(u); const L = norme(vec(pied, haut));
    out += trait(add(pied, n, 3.5), add(haut, n, 3.5), BOIS, 2.2) + trait(add(pied, n, -3.5), add(haut, n, -3.5), BOIS, 2.2);
    for (let k = 10; k < L - 4; k += 12) out += trait(add(add(pied, u, k), n, -3.5), add(add(pied, u, k), n, 3.5), BOIS, 1.6);
    // les trois mesures : la hauteur à gauche du mur, la distance sous le sol, la longueur le long de l'échelle
    const xh = X(0) - 16 - 12;
    if (mesures.h != null) out += cote([xh, Y(0)], [xh, Y(h)], [-1, 0], mesures.h, estCherche('h'), 'h');
    out += cote([X(0), Y(0) + 20], [X(d), Y(0) + 20], [0, 1], mesures.d, estCherche('d'), 'd');
    const M = [(pied[0] + haut[0]) / 2, (pied[1] + haut[1]) / 2];
    let nL = n; if ((M[0] - coin[0]) * nL[0] + (M[1] - coin[1]) * nL[1] < 0) nL = [-nL[0], -nL[1]];
    out += ecriture(aCote(M, nL, mesures.L, 10), mesures.L, estCherche('L'), 'L');
    if (angle) {
        // l'arc au pied de l'échelle, du sol (vers le mur) à l'échelle, plus petit que la distance au mur ;
        // la mesure DANS le triangle, entre le mur (et son angle droit) et l'échelle, juste au-dessus du sol
        // (vu en PNG : sur une échelle raide, la mesure sur la bissectrice tombait sur le mur)
        const theta = Math.atan2(h, d); const dpx = d * s;
        const r = Math.min(26, 0.75 * dpx); const ve = u;
        const a0 = add(pied, [-1, 0], r); const b0 = add(pied, ve, r);
        out += `<path d="M ${f1(a0[0])} ${f1(a0[1])} A ${f1(r)} ${f1(r)} 0 0 1 ${f1(b0[0])} ${f1(b0[1])}" fill="none" stroke="${estCherche('angle') ? COULEUR_CHERCHE : '#111827'}" stroke-width="1.6" data-code="angle"/>`;
        const yl = Y(0) - Math.max(18, r * Math.sin(theta / 2) + 6);
        const xEchelle = X(d) - ((Y(0) - yl) / (h * s)) * d * s;
        out += ecriture([(X(0) + 12 + xEchelle) / 2, yl], angle, estCherche('angle'), 'angle');
    }
    const aria = 'Une échelle posée contre un mur vertical, sur un sol horizontal ; l\'angle droit est codé au pied du mur.';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-mesures="${dataMesures(mesures)}" data-figure="pied:${d},0|haut:0,${h}|coin:0,0" role="img" aria-label="${aria}">${out}</svg>`;
};

/** Le troisième sommet T d'un triangle S(0 ; 0), R(0 ; a) tel que RT = b et ST = c, à droite du mur. */
export const troisiemeSommet = (a, b, c) => {
    const y = (a * a - b * b + c * c) / (2 * a);
    return [Math.sqrt(Math.max(c * c - y * y, 0)), y];
};

/**
 * L'étagère sur un mur vertical (Eduscol fin de 4e l. 430-431) : S en bas et R
 * en haut sur le mur (RS = a), l'étagère [RT] (RT = b), le support [ST]
 * (ST = c). L'étagère est horizontale si et seulement si l'angle en R est droit.
 * lettres = { coin: 'R', bas: 'S', bout: 'T' } ; mesures = { [nom de côté]: écriture }.
 */
export const svgEtagere = ({ a, b, c, lettres, mesures }) => {
    const T = troisiemeSommet(a, b, c);
    const padL = 84; const padR = 44; const padT = 42; const padB = 44;
    const s = Math.min((W - padL - padR) / T[0], (H - padT - padB) / (Math.max(a, T[1]) * 1.12));
    const x0 = padL + ((W - padL - padR) - T[0] * s) / 2;
    const X = (x) => x0 + x * s; const Y = (y) => H - padB - y * s;
    const pS = [X(0), Y(0)]; const pR = [X(0), Y(a)]; const pT = [X(T[0]), Y(T[1])];
    const { coin, bas, bout } = lettres;
    const nom = (p, q) => [p, q].sort().join('');
    let out = mur([X(0), Y(0) + 24], [X(0), Y(Math.max(a, T[1])) - 22]);
    // l'étagère (planche épaisse) et le support
    out += trait(pR, pT, BOIS, 6, ' stroke-linecap="butt"') + trait(pS, pT, BOIS, 3, ' stroke-linecap="round"');
    // mesures : RS à gauche du mur, RT au-dessus de la planche, ST en dessous du support
    const xg = X(0) - 16 - 12;
    out += cote([xg, pS[1]], [xg, pR[1]], [-1, 0], mesures[nom(coin, bas)], false, nom(coin, bas));
    const uRT = unitaire(vec(pR, pT)); let nRT = perp(uRT); if (nRT[1] > 0) nRT = [-nRT[0], -nRT[1]];
    out += ecriture(aCote([(pR[0] + pT[0]) / 2, (pR[1] + pT[1]) / 2], nRT, mesures[nom(coin, bout)], 8), mesures[nom(coin, bout)], false, nom(coin, bout));
    const uST = unitaire(vec(pS, pT)); let nST = perp(uST); if (nST[1] < 0) nST = [-nST[0], -nST[1]];
    out += ecriture(aCote([(pS[0] + pT[0]) / 2, (pS[1] + pT[1]) / 2], nST, mesures[nom(bas, bout)], 8), mesures[nom(bas, bout)], false, nom(bas, bout));
    out += point(pR, coin, [0.6, -1]) + point(pS, bas, [0.7, 0.9]) + point(pT, bout, [1, 0.2]);
    const aria = `Une étagère posée sur un mur vertical : ${coin} et ${bas} sur le mur, ${bout} au bout de l'étagère, un support de ${bas} à ${bout}.`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-mesures="${dataMesures(mesures)}" data-figure="${bas}:0,0|${coin}:0,${a}|${bout}:${T[0].toFixed(6)},${T[1].toFixed(6)}" role="img" aria-label="${aria}">${out}</svg>`;
};

/**
 * Le mur des maçons (fiche 8 du professeur) : I au pied du mur, H sur le mur
 * (IH = a), S sur le sol (IS = b), la mesure HS = c au mètre ruban. Le mur est
 * droit si et seulement si l'angle en I est droit.
 * lettres = { pied: 'I', haut: 'H', sol: 'S' } ; mesures = { [nom de côté]: écriture }.
 */
export const svgMur = ({ a, b, c, lettres, mesures }) => {
    // I(0 ; 0), S(b ; 0), H à a de I et c de S, au-dessus du sol
    const xH = (a * a + b * b - c * c) / (2 * b);
    const Hh = [xH, Math.sqrt(Math.max(a * a - xH * xH, 0))];
    const padL = 84; const padR = 40; const padT = 34; const padB = 48;
    const xmin = Math.min(0, xH); const xmax = b;
    const s = Math.min((W - padL - padR) / (xmax - xmin), (H - padT - padB) / (Hh[1] * 1.15));
    const x0 = padL + ((W - padL - padR) - (xmax - xmin) * s) / 2 - xmin * s;
    const X = (x) => x0 + x * s; const Y = (y) => H - padB - y * s;
    const pI = [X(0), Y(0)]; const pS = [X(b), Y(0)]; const pH = [X(Hh[0]), Y(Hh[1])];
    const { pied, haut, sol: nomSol } = lettres;
    const nom = (p, q) => [p, q].sort().join('');
    const uIH = unitaire(vec(pI, pH));
    let out = mur(pI, add(pH, uIH, 20));
    out += sol(X(0) - 30, X(b) + 26, Y(0));
    out += trait(pH, pS, GRIS, 1.6, ' stroke-dasharray="6 4"');
    // mesures : IH le long du mur (à gauche), IS sous le sol, HS au-dessus du ruban
    const nG = gauche(uIH);
    out += cote(add(pI, nG, 28), add(pH, nG, 28), [-1, 0], mesures[nom(pied, haut)], false, nom(pied, haut));
    out += cote([pI[0], pI[1] + 20], [pS[0], pS[1] + 20], [0, 1], mesures[nom(pied, nomSol)], false, nom(pied, nomSol));
    const uHS = unitaire(vec(pH, pS)); let nHS = perp(uHS); if (nHS[1] > 0) nHS = [-nHS[0], -nHS[1]];
    out += ecriture(aCote([(pH[0] + pS[0]) / 2, (pH[1] + pS[1]) / 2], nHS, mesures[nom(haut, nomSol)], 8), mesures[nom(haut, nomSol)], false, nom(haut, nomSol));
    out += point(pI, pied, [0.8, -1]) + point(pH, haut, [1, -0.4]) + point(pS, nomSol, [0.4, -1]);
    const aria = `Un mur posé sur un sol horizontal : ${pied} au pied du mur, ${haut} sur le mur, ${nomSol} sur le sol ; la longueur ${haut}${nomSol} est mesurée au mètre ruban.`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-mesures="${dataMesures(mesures)}" data-figure="${pied}:0,0|${nomSol}:${b},0|${haut}:${Hh[0].toFixed(6)},${Hh[1].toFixed(6)}" role="img" aria-label="${aria}">${out}</svg>`;
};
